/**
 * Runtime-validated trip document. Seed JSON is mapped into this schema.
 * Inputs: unknown JSON from storage/API. Outputs: TripDocument.
 */

import * as z from "zod";

export const SCHEMA_VERSION = 1 as const;

export const MoneySchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  amountMinor: z.number().int(),
});

export const AllocationSchema = z.object({
  householdId: z.string().min(1),
  amountMinor: z.number().int(),
});

export const PaymentTermsSchema = z.object({
  kind: z.enum(["expected_date", "tentative_date", "pay_at_property", "unknown", "confirmed_deadline"]),
  date: z.string().nullable().optional(),
  display: z.string().nullable().optional(),
  evidence: z.string().nullable().optional(),
  collectionMethod: z.string().nullable().optional(),
  hardDeadline: z.boolean().optional(),
  reminderTimezone: z.string().optional(),
});

export const PaymentBaselineSchema = z.object({
  status: z.enum(["unpaid_owner_reported", "unknown", "partial", "paid"]),
  asOf: z.string().optional(),
  sourceId: z.string().optional(),
});

export const HouseholdSchema = z.object({
  id: z.string(),
  label: z.string(),
  adults: z.number().int().nonnegative(),
  children: z.number().int().nonnegative(),
});

export const PlaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string().nullable().optional(),
  thaiName: z.string().nullable().optional(),
  coordinates: z
    .object({
      lat: z.number(),
      lng: z.number(),
      verified: z.boolean(),
      source: z.string().optional(),
      asOf: z.string().optional(),
    })
    .nullable()
    .optional(),
  mapsUrl: z.string().nullable().optional(),
  precision: z.string().nullable().optional(),
  contactId: z.string().nullable().optional(),
  enrichmentStatus: z.string().optional(),
  sourceIds: z.array(z.string()).default([]),
  imageAssetId: z.string().nullable().optional(),
});

export const StaySchema = z.object({
  id: z.string(),
  placeId: z.string(),
  chapter: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  nights: z.number().int(),
  timezone: z.string(),
  checkInFrom: z.string().nullable().optional(),
  checkInUntil: z.string().nullable().optional(),
  checkOutBy: z.string().nullable().optional(),
  plannedDepartureLocal: z.string().nullable().optional(),
  plannedDepartureEvidence: z.string().nullable().optional(),
  roomMoveRequired: z.boolean().nullable().optional(),
  dateEvidence: z.string().optional(),
  notes: z.array(z.string()).default([]),
  sourceIds: z.array(z.string()).default([]),
  imageAssetId: z.string().nullable().optional(),
});

export const BookingKindSchema = z.enum([
  "hotel_reservation",
  "excursion",
  "transfer_package",
  "flight",
  "other",
]);

export const BookingStatusSchema = z.enum([
  "idea",
  "planned",
  "booked_owner_reported",
  "booked_chat_agreement",
  "booked_chat_confirmation",
  "booked_supplier_confirmed",
  "cancelled",
]);

export const BookingSchema = z.object({
  id: z.string(),
  kind: BookingKindSchema,
  title: z.string().optional(),
  provider: z.string().nullable().optional(),
  stayId: z.string().nullable().optional(),
  householdId: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  roomName: z.string().nullable().optional(),
  bookingStatus: BookingStatusSchema,
  confirmation: z.string().nullable().optional(),
  expediaItinerary: z.string().nullable().optional(),
  occupancy: z
    .object({
      adults: z.number().int(),
      children: z.number().int(),
    })
    .nullable()
    .optional(),
  breakfastIncluded: z.boolean().optional(),
  beverageCredit: z
    .object({
      currency: z.string(),
      amountMinor: z.number().int(),
      basis: z.string(),
      deductFromCost: z.literal(false),
    })
    .nullable()
    .optional(),
  nightlyAmountsMinor: z.array(z.number().int()).optional(),
  taxMinor: z.number().int().optional(),
  freeCancellationUntil: z.string().nullable().optional(),
  cancellationTerms: z.string().nullable().optional(),
  cost: MoneySchema.nullable(),
  allocations: z.array(AllocationSchema).default([]),
  allocationStatus: z.string().optional(),
  paymentBaseline: PaymentBaselineSchema.optional(),
  paymentTerms: PaymentTermsSchema.optional(),
  indicativeEquivalent: z
    .object({
      currency: z.string(),
      amountMinor: z.number().int(),
      source: z.string(),
      asOf: z.string(),
      includeInTotals: z.literal(false),
    })
    .nullable()
    .optional(),
  priceBasis: z
    .object({
      adultCount: z.number().int(),
      adultAmountMinor: z.number().int(),
      paidChildCount: z.number().int(),
      childAmountMinor: z.number().int(),
      freeChildCount: z.number().int(),
      ageException: z.string().optional(),
    })
    .optional(),
  vehicle: z.string().nullable().optional(),
  passengers: z.number().int().optional(),
  included: z.array(z.string()).optional(),
  boatIncluded: z.boolean().nullable().optional(),
  dietaryNotes: z.array(z.string()).optional(),
  hotelPickupConfirmed: z.boolean().nullable().optional(),
  contactId: z.string().nullable().optional(),
  notes: z.array(z.string()).default([]),
  sourceIds: z.array(z.string()).default([]),
  attachmentIds: z.array(z.string()).default([]),
});

export const FlightEndpointSchema = z.object({
  airport: z.string(),
  local: z.string(),
  timezone: z.string(),
  instant: z.string(),
  terminal: z.string().nullable().optional(),
  gate: z.string().nullable().optional(),
});

export const FlightSchema = z.object({
  id: z.string(),
  kind: z.literal("flight"),
  number: z.string(),
  airline: z.string(),
  departure: FlightEndpointSchema,
  arrival: FlightEndpointSchema,
  evidenceStatus: z.string(),
  pnr: z.string().nullable().optional(),
  seats: z.string().nullable().optional(),
  baggage: z.string().nullable().optional(),
  cost: MoneySchema.nullable().optional(),
  paymentStatus: z.enum(["unknown", "unpaid", "paid", "partial"]).default("unknown"),
  notes: z.array(z.string()).default([]),
  sourceIds: z.array(z.string()).default([]),
  attachmentIds: z.array(z.string()).default([]),
  progressStatus: z.enum(["scheduled", "complete"]).optional(),
});

export const EventStopSchema = z.object({
  time: z.string().nullable().optional(),
  description: z.string(),
});

export const TripEventSchema = z.object({
  id: z.string(),
  kind: z.enum(["excursion", "transfer", "flight", "stay_window", "free_time", "other"]),
  bookingId: z.string().nullable().optional(),
  flightId: z.string().nullable().optional(),
  stayId: z.string().nullable().optional(),
  title: z.string().optional(),
  date: z.string(),
  timezone: z.string(),
  startLocal: z.string().nullable().optional(),
  endLocal: z.string().nullable().optional(),
  startConfidence: z.string().nullable().optional(),
  endConfidence: z.string().nullable().optional(),
  timeConfidence: z.string().nullable().optional(),
  dateConfidence: z.string().nullable().optional(),
  meetingPlaceId: z.string().nullable().optional(),
  meetingConfidence: z.string().nullable().optional(),
  meetingNote: z.string().nullable().optional(),
  fromAirport: z.string().nullable().optional(),
  toPlaceId: z.string().nullable().optional(),
  fromPlaceId: z.string().nullable().optional(),
  linkedFlightId: z.string().nullable().optional(),
  plannedDeparture: z.string().nullable().optional(),
  plannedArrival: z.string().nullable().optional(),
  pickupMode: z.string().nullable().optional(),
  pickupInstruction: z.string().nullable().optional(),
  planStatus: z.enum(["idea", "planned", "booked", "cancelled"]).optional(),
  transferMethod: z.enum(["grab_bolt", "taxi", "arranged_driver", "other"]).nullable().optional(),
  includesBoat: z.boolean().nullable().optional(),
  flexible: z.boolean().optional(),
  stops: z.array(EventStopSchema).default([]),
  notes: z.array(z.string()).default([]),
  sourceIds: z.array(z.string()).default([]),
  archived: z.boolean().optional(),
  sortIndex: z.number().int().optional(),
  progressStatus: z.enum(["scheduled", "complete"]).optional(),
});

export const ContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  phone: z.string().nullable().optional(),
  whatsapp: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  verified: z.boolean().default(false),
  sourceIds: z.array(z.string()).default([]),
});

export const SupplierPaymentSchema = z.object({
  id: z.string(),
  bookingId: z.string(),
  payerHouseholdId: z.string(),
  credit: MoneySchema,
  actualCharge: MoneySchema.nullable().optional(),
  date: z.string(),
  fundedAllocations: z.array(AllocationSchema),
  note: z.string().nullable().optional(),
  idempotencyKey: z.string(),
  createdAt: z.string(),
  createdBy: z.string().default("owner session"),
});

export const SupplierRefundSchema = z.object({
  id: z.string(),
  bookingId: z.string(),
  paymentId: z.string().nullable().optional(),
  credit: MoneySchema,
  recipientHouseholdId: z.string().nullable().optional(),
  reversedAllocations: z.array(AllocationSchema).default([]),
  cancellationCharge: MoneySchema.nullable().optional(),
  date: z.string(),
  note: z.string().nullable().optional(),
  idempotencyKey: z.string(),
  createdAt: z.string(),
  createdBy: z.string().default("owner session"),
});

export const HouseholdSettlementSchema = z.object({
  id: z.string(),
  fromHouseholdId: z.string(),
  toHouseholdId: z.string(),
  amount: MoneySchema,
  date: z.string(),
  relatedPaymentIds: z.array(z.string()).default([]),
  unallocated: z.boolean().default(false),
  note: z.string().nullable().optional(),
  idempotencyKey: z.string(),
  createdAt: z.string(),
  createdBy: z.string().default("owner session"),
});

export const AttachmentSchema = z.object({
  id: z.string(),
  storageKey: z.string(),
  filename: z.string(),
  contentType: z.string(),
  bytes: z.number().int().nonnegative(),
  sha256: z.string().optional(),
  linkedEntityIds: z.array(z.string()).default([]),
  sourceId: z.string().nullable().optional(),
  createdAt: z.string(),
});

export const ReviewItemSchema = z.object({
  id: z.string(),
  message: z.string(),
  entityIds: z.array(z.string()).default([]),
  status: z.string(),
  resolution: z.string().nullable().optional(),
  dismissed: z.boolean().default(false),
  dismissedNote: z.string().nullable().optional(),
});

export const IdeaSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string(),
  date: z.string().nullable().optional(),
  placeId: z.string().nullable().optional(),
  cost: MoneySchema.nullable().optional(),
  notes: z.string().nullable().optional(),
});

export const PreparationItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  notes: z.string().nullable().optional(),
  dueDate: z.string().nullable().optional(),
  linkedEntityIds: z.array(z.string()).default([]),
  ownerOnlyShared: z.boolean().default(true),
});

export const ThaiPhraseSchema = z.object({
  id: z.string(),
  english: z.string(),
  thai: z.string(),
  romanization: z.string(),
  translationReviewed: z.boolean().default(false),
  kind: z.enum(["allergy", "dietary", "phrase"]).optional(),
  title: z.string().optional(),
  reviewedBy: z.string().nullable().optional(),
  reviewedAt: z.string().nullable().optional(),
  backTranslation: z.string().optional(),
  draftProvider: z.enum(["dictionary", "google", "anthropic", "openai", "owner"]).optional(),
});

export const MemorySchema = z.object({
  id: z.string(),
  date: z.string(),
  caption: z.string(),
  attachmentId: z.string().nullable().optional(),
  createdAt: z.string(),
});

export const SourceSchema = z.object({
  id: z.string(),
  kind: z.string(),
  originalFilename: z.string().nullable().optional(),
  sha256: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  localTime: z.string().nullable().optional(),
});

export const RevisionSummarySchema = z.object({
  version: z.number().int(),
  at: z.string(),
  summary: z.string(),
  author: z.string(),
  snapshotKey: z.string(),
});

export const LiveObservationSchema = z.object({
  id: z.string(),
  provider: z.string(),
  subjectId: z.string(),
  observedAt: z.string().nullable().optional(),
  fetchedAt: z.string(),
  expiresAt: z.string().nullable().optional(),
  status: z.enum(["ok", "stale", "unavailable", "error", "not_yet_in_range", "rate_limited"]),
  payload: z.unknown().optional(),
  error: z.string().nullable().optional(),
});

export const TripSettingsSchema = z.object({
  id: z.string(),
  title: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  travellerCount: z.number().int(),
  diaryTimezone: z.string(),
  homeTimezone: z.string(),
  displayCurrency: z.string(),
  chapterOrder: z.array(z.string()),
  subtitle: z.string().optional(),
  previewNow: z.string().nullable().optional(),
  insuranceNote: z.string().nullable().optional(),
});

export const TripDocumentSchema = z.object({
  schemaVersion: z.literal(SCHEMA_VERSION),
  fixtureKind: z.enum(["family", "synthetic"]).default("family"),
  fixtureMarker: z.string().nullable().optional(),
  trip: TripSettingsSchema,
  households: z.array(HouseholdSchema),
  places: z.array(PlaceSchema),
  stays: z.array(StaySchema),
  bookings: z.array(BookingSchema),
  flights: z.array(FlightSchema),
  events: z.array(TripEventSchema),
  contacts: z.array(ContactSchema),
  supplierPayments: z.array(SupplierPaymentSchema),
  supplierRefunds: z.array(SupplierRefundSchema),
  householdSettlements: z.array(HouseholdSettlementSchema),
  attachments: z.array(AttachmentSchema),
  reviewItems: z.array(ReviewItemSchema),
  ideas: z.array(IdeaSchema),
  preparationItems: z.array(PreparationItemSchema),
  thaiPhrases: z.array(ThaiPhraseSchema).default([]),
  memories: z.array(MemorySchema),
  sources: z.array(SourceSchema),
  revisions: z.array(RevisionSummarySchema),
  liveObservations: z.array(LiveObservationSchema),
  sourceIdMap: z.record(z.string(), z.string()).default({}),
  protectedEntityIds: z.array(z.string()).default([]),
  paymentBaselineNote: z.string().optional(),
  controlTotals: z
    .object({
      hotelGBPMinor: z.number().int(),
      hotelTHBMinor: z.number().int(),
      allKnownGBPMinor: z.number().int(),
      allKnownTHBMinor: z.number().int(),
      completeHolidayTotal: z.literal(false),
      notes: z.string().optional(),
    })
    .optional(),
  revision: z.number().int().nonnegative(),
  updatedAt: z.string(),
});

export type MoneyValue = z.infer<typeof MoneySchema>;
export type Allocation = z.infer<typeof AllocationSchema>;
export type Booking = z.infer<typeof BookingSchema>;
export type Flight = z.infer<typeof FlightSchema>;
export type TripEvent = z.infer<typeof TripEventSchema>;
export type Stay = z.infer<typeof StaySchema>;
export type Place = z.infer<typeof PlaceSchema>;
export type Contact = z.infer<typeof ContactSchema>;
export type SupplierPayment = z.infer<typeof SupplierPaymentSchema>;
export type SupplierRefund = z.infer<typeof SupplierRefundSchema>;
export type HouseholdSettlement = z.infer<typeof HouseholdSettlementSchema>;
export type ReviewItem = z.infer<typeof ReviewItemSchema>;
export type TripDocument = z.infer<typeof TripDocumentSchema>;
export type Household = z.infer<typeof HouseholdSchema>;
export type Attachment = z.infer<typeof AttachmentSchema>;
export type Idea = z.infer<typeof IdeaSchema>;
export type ThaiPhrase = z.infer<typeof ThaiPhraseSchema>;
export type Memory = z.infer<typeof MemorySchema>;
export type LiveObservation = z.infer<typeof LiveObservationSchema>;

export function emptyTripDocument(partial?: Partial<TripDocument>): TripDocument {
  const now = new Date().toISOString();
  return TripDocumentSchema.parse({
    schemaVersion: SCHEMA_VERSION,
    fixtureKind: "family",
    trip: {
      id: "new-trip",
      title: "Thailand, together",
      startDate: "2026-10-22",
      endDate: "2026-11-08",
      travellerCount: 6,
      diaryTimezone: "Asia/Bangkok",
      homeTimezone: "Europe/London",
      displayCurrency: "GBP",
      chapterOrder: ["Chiang Mai", "Hang Dong", "Koh Samet", "Bangkok"],
      subtitle: "The Reed family",
    },
    households: [
      { id: "chris", label: "Chris", adults: 2, children: 2 },
      { id: "paul", label: "Paul", adults: 2, children: 0 },
    ],
    places: [],
    stays: [],
    bookings: [],
    flights: [],
    events: [],
    contacts: [],
    supplierPayments: [],
    supplierRefunds: [],
    householdSettlements: [],
    attachments: [],
    reviewItems: [],
    ideas: [],
    preparationItems: [],
    thaiPhrases: [],
    memories: [],
    sources: [],
    revisions: [],
    liveObservations: [],
    sourceIdMap: {},
    revision: 0,
    updatedAt: now,
    ...partial,
  });
}
